import React, { useState } from 'react';
import {
    Acousticness,
    BaseScheduledGenerativePathScore,
    CoreEmotionalAtmosphere,
    isScheduledGenerativeDeepenPathScore,
    PathScore,
    PathScoreModes,
    ScheduledExperimentalPathScore,
    ScheduledGenerativeDeepenPathScore,
    SessionScoreEmotionalIntensity,
    TherapeuticDirection,
    Wavepath,
} from 'wavepaths-shared/core';

import { DropdownControl, SegmentedControl } from '@/component-library';

import { usePathScoreDurationSelector } from '.';
import * as infoCopy from './infoCopy';
import * as pathScoreFns from './pathScoreFns';

export interface SelectorState {
    mode?: PathScoreModes;
    emotion?: CoreEmotionalAtmosphere;
    tone?: Acousticness;
    intensity?: SessionScoreEmotionalIntensity;
    music?: string;
    preferredDuration: number | 'Auto';
}

const selectorStateDefault: SelectorState = {
    emotion: CoreEmotionalAtmosphere.STILLNESS,
    tone: 'Mixed',
    intensity: SessionScoreEmotionalIntensity.LOW,
    music: 'Any',
    preferredDuration: pathScoreFns.AUTO_VALUE,
};

export type DeepenPathScore =
    | BaseScheduledGenerativePathScore
    | ScheduledGenerativeDeepenPathScore
    | ScheduledExperimentalPathScore;

interface TonalDeepenWaveSelectorProps {
    pathScores: DeepenPathScore[];
    wavepath: Wavepath;
    onUpdateWave: (updatedWavepath: Wavepath) => void;
}

export const isDeepenPathScore = (p: PathScore): p is DeepenPathScore => p.direction === TherapeuticDirection.DEEPEN;

function TonalDeepenWaveSelector({ pathScores, wavepath, onUpdateWave }: TonalDeepenWaveSelectorProps): JSX.Element {
    const filteredPathScores = pathScores.filter(isScheduledGenerativeDeepenPathScore);

    const [emotion, setEmotion] = useState(
        isScheduledGenerativeDeepenPathScore(wavepath.pathScore)
            ? wavepath.pathScore.emotion
            : selectorStateDefault.emotion,
    );
    const emotionFilteredScores = pathScoreFns.filterScoresByEmotion(filteredPathScores, emotion);

    const defaultTone =
        'acousticness' in wavepath.pathScore ? wavepath.pathScore.acousticness : selectorStateDefault.tone;
    const [tone, setTone] = useState(defaultTone);
    const toneColourFilteredScores = pathScoreFns.filterScoresByTone(emotionFilteredScores, tone);

    if (toneColourFilteredScores.length == 0) {
        setTone(emotionFilteredScores[0].acousticness || 'Mixed');
    }

    const defaultMusic = 'music' in wavepath.pathScore ? wavepath.pathScore.music : selectorStateDefault.music;
    const [music, setMusic] = useState(defaultMusic);
    const preDurationFilteredScores = pathScoreFns.filterScoresByMusic(toneColourFilteredScores, music);
    if (preDurationFilteredScores.length == 0 && toneColourFilteredScores[0]) {
        setMusic(toneColourFilteredScores[0].music ?? 'Any');
    }

    const { durationOptions, preferredDuration, setPreferredDuration } = usePathScoreDurationSelector({
        wavepath,
        onUpdateWave,
        preDurationFilteredScores,
        dependencies: [emotion, tone, music],
    });

    return (
        <>
            <SegmentedControl
                name="wave-emotion"
                heading={'Emotionality'}
                canSave={false}
                size="small"
                colour="dark"
                options={pathScoreFns.convertListToOptions(
                    [
                        CoreEmotionalAtmosphere.STILLNESS,
                        CoreEmotionalAtmosphere.VITALITY,
                        CoreEmotionalAtmosphere.BITTERSWEET,
                        CoreEmotionalAtmosphere.TENSION,
                    ],
                    pathScoreFns.extractEmotionsFromScores(filteredPathScores),
                )}
                value={emotion}
                onChange={setEmotion}
                info={infoCopy.emotion}
            />
            <SegmentedControl
                name="wave-tone"
                heading={'Tone'}
                canSave={false}
                size="small"
                colour="dark"
                options={pathScoreFns.convertListToOptions(
                    ['Acoustic', 'Mixed', 'Electronic'] as Acousticness[],
                    pathScoreFns.extractToneColoursFromScores(emotionFilteredScores),
                )}
                value={tone}
                onChange={setTone}
                info={infoCopy.tone}
            />
            <DropdownControl
                name="wave-music"
                heading={'Music'}
                canSave={false}
                size="small"
                colour="dark"
                options={pathScoreFns.convertListToOptions(
                    [...pathScoreFns.extractMusicOptionsFromScores(toneColourFilteredScores)],
                    pathScoreFns.extractMusicOptionsFromScores(toneColourFilteredScores),
                )}
                value={music}
                onChange={setMusic}
                info={infoCopy.composition}
            />
            <DropdownControl
                name="wave-duration"
                heading={'Duration'}
                canSave={false}
                size="small"
                colour="dark"
                options={durationOptions}
                value={preferredDuration}
                onChange={setPreferredDuration}
                info={infoCopy.duration}
            />
        </>
    );
}

export default TonalDeepenWaveSelector;
